import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { setNavLight, setNavDark } from "@/app/utils/nav";

gsap.registerPlugin(ScrollTrigger);

interface UseSectionNavThemeOptions {
  trigger: string | HTMLElement;
  isDark: boolean;
  start?: string;
  end?: string | (() => string);
  markers?: boolean;
  onEnter?: () => void;
  onLeave?: () => void;
  onEnterBack?: () => void;
  onLeaveBack?: () => void;
}

/**
 * Custom hook for managing navbar light/dark theme based on section scroll position
 * @param options Configuration options for the scroll trigger and theme
 */
export const useSectionNavTheme = ({
  trigger,
  isDark,
  start = "top 40px",
  end,
  markers = false,
}: UseSectionNavThemeOptions) => {
  useGSAP(() => {
    // Check if dark mode is active by checking the 'dark' class on html element
    const isDarkMode = () =>
      document.documentElement.classList.contains("dark");

    // Resolve end value - if it's a function, call it; otherwise use the string
    const resolvedEnd = typeof end === "function" ? end() : end;

    // Create ScrollTrigger for navbar theme toggle
    gsap.timeline({
      scrollTrigger: {
        trigger,
        start,
        end: resolvedEnd,
        markers,
        onEnter: () => {
          // If dark mode is active, always use light navbar
          // Otherwise, respect the isDark prop
          if (isDarkMode()) {
            setNavLight();
          } else {
            if (isDark) {
              setNavDark();
            } else {
              setNavLight();
            }
          }
        },
        onEnterBack: () => {
          // Same logic for onEnterBack
          if (isDarkMode()) {
            setNavLight();
          } else {
            if (isDark) {
              setNavDark();
            } else {
              setNavLight();
            }
          }
        },
      },
    });

    // Listen for dark mode changes (if user toggles dark mode while scrolled)
    const handleDarkModeChange = () => {
      ScrollTrigger.refresh();
    };

    // Watch for changes to the dark class
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          handleDarkModeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [trigger, isDark, start, end, markers]);
};
